/**
 * JarvisRootProvider
 *
 * Wrappt die gesamte App mit dem `<ConversationProvider>` von
 * `@elevenlabs/react`, damit die Voice-Session NICHT bei jedem
 * Routen-Wechsel unmounted wird.
 *
 * Aktuelles Layout-Setup: `<Layout>` ist nicht persistent — es wird
 * pro Page neu gemountet. Dadurch verschwindet jeder Provider, der
 * INSIDE des Layouts lebt, beim Modul-Wechsel — und mit ihm die
 * laufende WebRTC-Connection zu ElevenLabs.
 *
 * Lösung: Provider hier auf App-Root-Ebene. Side-effects sind null,
 * solange niemand `startSession()` ruft — bei Standalone-Orgs also
 * komplett unsichtbar/lautlos.
 *
 * Zusätzlich: nutzt `useJarvisRouteContext` um bei Route-Wechseln den
 * aktuellen Seitenkontext (Kunde/Anfrage/Projekt) an das Backend zu
 * pushen. Dadurch kann Kiki bei vagen Sprachanfragen wie „erstell mir
 * eine Rechnung über 250 Euro" mit dem AKTUELLEN Kunden arbeiten, ohne
 * dass der Nutzer den Namen wiederholen muss. Backend-Endpoint:
 * `POST /api/heykiki/jarvis/context`.
 */

import { ConversationProvider } from '@elevenlabs/react';
import { useAuth } from '../../../context/AuthContext';
import useJarvisRouteContext from './useJarvisRouteContext';
// Workflow-Feed-Hook (`useJarvisWorkflowFeed`) ist gebaut und im Backend
// einsatzbereit, aber das Chat-Widget rendert die Workflow-Cards aktuell
// nicht — der User stimmt sich mit Luca ab wie die UX final aussehen
// soll. Re-Aktivierung später: Import + Hook-Call hier wieder einbauen.

function JarvisRouteContextTracker() {
  const { user } = useAuth();
  // Nur für HeyKiki-Suborgs pushen — Standalone-Werkpilot-Orgs haben
  // gar keinen Jarvis-Agent, da ist der Endpoint-Call sinnloser Traffic.
  const isHeykiki = user?.organisation?.orgType === 'HEYKIKI';
  useJarvisRouteContext({ enabled: isHeykiki });
  return null; // reiner Effect-Hook, kein Render
}

export default function JarvisRootProvider({ children }) {
  return (
    <ConversationProvider>
      <JarvisRouteContextTracker />
      {children}
    </ConversationProvider>
  );
}
